<template>
  <div v-loading="loading" class="text-gray-600">
    <div class="no-padding-container container mb-20">
      <SingleDesignHero
        v-bind="singleDesignHeroProps"
        :title="$t('Lookbook.Lookbook')"
        @back="lookbookRedirect(orientationId)"
        @next="redirectToSingleBoxerPage($event)"
        @previous="redirectToSingleBoxerPage($event)"
        @click="chooseMembership"
      />
    </div>
    <div class="no-padding-container container md:mb-5" :class="paddingClass">
      <LookbookGrid v-bind="lookbookGridProps" @click="chooseMembership" />
    </div>
    <ReadyToTry class="hidden md:block md:mb-28" @click="chooseMembership" />
    <div class="no-padding-container container mt-5">
      <DiscoverMemberships
        :title="$t('Lookbook.LookingForSomethingDifferent')"
        :description="$t('Lookbook.DiscoverOtherMemberships')"
        :color="design.primaryColor"
        :button-text="$t('Lookbook.Discover')"
        :orientation-id="discoverMemberShipsOrientationId"
        :custom-images="studioImages"
        @click="lookbookRedirect($event.id)"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { GroupIdsByName, OrientationsByGroupIds, OrientationSlugsByGroupIds, Orientations, OrientationsNamesByGroupIds, ProductCategoryIds, ProductTypes } from '@/enums/products'
import { QueryParams as SignupQueryParams } from '@/enums/signup'
import { QueryParams as GeneralQueryParams, ScreenBreakpoints } from '@/enums/general'
import { GET_SINGLE_DESIGN } from '@/utils/store/actionTypes'
import { AUTH_USER } from '@/utils/store/getterTypes'
import config from '@/config/general'
import { convertSrcSet, capitalize, getImagePaths, convertSrc } from '@/utils/general'
import isEmpty from 'lodash/isEmpty'
import { vueWindowSizeMixin } from 'vue-window-size'
const DiscoverMemberships = () => import('@/components/DiscoverMemberships')
const ReadyToTry = () => import('@/components/ReadyToTry')
const LookbookGrid = () => import('@/components/LookbookGrid')
const SingleDesignHero = () => import('@/components/SingleDesignHero')

export default {
  name: 'SingleBoxerLookbook',
  layout: 'homepage',
  components: {
    DiscoverMemberships,
    LookbookGrid,
    SingleDesignHero,
    ReadyToTry
  },
  mixins: [vueWindowSizeMixin],
  async fetch () {
    this.loading = true

    try {
      const design = await this.getSingleDesign({ slug: this.boxerName.replace(' ', '-'), product_group: this.$route.params.orientation })

      this.design = design
    } catch (error) {
      this.$ota.general.logError(error)
      this.$otoaster.danger(this.$t('Error.general'))
    }

    this.loading = false
  },
  data () {
    return {
      loading: false,
      design: {}
    }
  },
  computed: {
    discoverMemberShipsOrientationId () {
      return GroupIdsByName[this.$route.params.orientation] === GroupIdsByName.socks ? GroupIdsByName[Orientations.boysoriginals] : GroupIdsByName[this.$route.params.orientation]
    },
    orientationId () {
      return GroupIdsByName[this.$route.params.orientation]
    },
    isMobile () {
      return this.windowWidth < ScreenBreakpoints.md
    },
    image () {
      return convertSrcSet(config.defaultProductImage)
    },
    studioImages () {
      if (!this.design) {
        return undefined
      }

      const isAfterShowDynamicImagesDate = this.$moment('2020-12-15') < this.$moment(this.design.releaseDate)

      if (!isAfterShowDynamicImagesDate) {
        return undefined
      }

      return {
        [GroupIdsByName[Orientations.menoriginals]]: this.getStudioImage({ slug: this.boxerName, orientation: Orientations.menoriginals }),
        [GroupIdsByName[Orientations.menone]]: this.getStudioImage({ slug: this.boxerName, orientation: Orientations.menone }),
        [GroupIdsByName[Orientations.boysoriginals]]: this.getStudioImage({ slug: this.boxerName, orientation: Orientations.boysoriginals })
      }
    },
    boxerName () {
      return this.$route.params.slug
    },
    date () {
      return this.design.releaseDate ? `${this.$t(`Months[${this.$moment(this.design.releaseDate).format('M') - 1}]`)} ‘${this.$moment(this.design.releaseDate).format('YY')}` : ''
    },
    testImage () {
      return convertSrcSet({
        mobile: {
          '1x': '/img/lookbook/test-men-one-detail-image-mob.webp',
          '2x': '/img/lookbook/test-men-one-detail-image-mob@2x.webp'
        },
        desktop: {
          '1x': '/img/lookbook/test-men-one-detail-image.webp',
          '2x': '/img/lookbook/test-men-one-detail-image@2x.webp'
        }
      })
    },
    primaryColor () {
      return this.design.primaryColor ? `rgb(${this.design.primaryColor})` : 'transparent'
    },
    variant () {
      return OrientationsByGroupIds[this.orientationId] ?? 'default'
    },
    isMenOne () {
      return this.variant === Orientations.menone
    },
    isBoys () {
      return this.variant === Orientations.boysoriginals
    },
    isMenOriginals () {
      return this.variant === Orientations.menoriginals
    },
    paddingClass () {
      return this.isMenOne ? 'lg:mb-48' : 'lg:mb-28'
    },
    designDateIsPastLastLookBookVideo () {
      if (this.isSocksVariant) {
        return this.$moment(this.design.releaseDate) > this.$moment('01-01-2024', 'DD-MM-YYYY')
      }

      return this.$moment(this.design.releaseDate) > this.$moment('31-01-2024', 'DD-MM-YYYY')
    },
    hasVideo () {
      return (this.orientationId === GroupIdsByName[Orientations.menoriginals] ||
      this.orientationId === GroupIdsByName[Orientations.socksoriginals]) &&
      this.$moment(this.design.releaseDate) >= this.$moment('01-01-2019', 'DD-MM-YYYY') &&
      !this.designDateIsPastLastLookBookVideo
    },
    singleDesignHeroProps () {
      return isEmpty(this.design) ? {
        design: {
          previousDesignSlug: '',
          nextDesignSlug: '',
          name: '',
          date: '',
          description: '',
          color: '',
          image: {}
        }
      } : {
        design: {
          previousDesignSlug: this.design?.metadata?.previousDesignSlug || '',
          nextDesignSlug: this.design?.metadata?.nextDesignSlug || '',
          name: this.design.name,
          date: this.date,
          color: this.primaryColor,
          description: this.design.descriptions[this.$route.params.orientation],
          image: getImagePaths({
            s3: true,
            path: `/images/design/${this.boxerName}/products/${this.boxerName}-${this.$route.params.orientation}${this.$route.params.orientation === Orientations.socksoriginals ? '' : '-boxershort'}`,
            noMobile: true
          })
        }
      }
    },
    slogan () {
      if (this.isMenOne) {
        return this.$t('Lookbook.DefaultMenOneSlogan')
      }

      if (this.design && this.design.slogan) {
        return this.design.slogan
      }

      return this.$t('Lookbook.DefaultSlogan')
    },
    placeholderImage () {
      if (this.hasVideo) {
        return convertSrc(`/images/design/${this.boxerName}/video/${this.boxerName}-${this.$route.params.orientation}.webp`, true)
      }

      if (!this.isMenOne && !this.isBoys && this.designDateIsPastLastLookBookVideo) {
        return convertSrc(`/images/design/${this.boxerName}/video/${this.boxerName}-men-originals.webp`, true)
      }

      return convertSrc(`/images/design/${this.boxerName}/detail-image/${this.boxerName}-${this.$route.params.orientation}.webp`, true)
    },
    squareImage () {
      if (this.isMenOriginals || this.isSocksVariant) {
        return getImagePaths({
          s3: true,
          noMobile: true,
          path: `/images/design/${this.boxerName}/video/${this.boxerName}-${this.$route.params.orientation}`
        })
      }

      return getImagePaths({
        s3: true,
        path: `/images/design/${this.boxerName}/detail-image/${this.boxerName}-${this.$route.params.orientation}`
      })
    },
    isSocksVariant () {
      return this.variant === Orientations.socksoriginals
    },
    lookbookGridProps () {
      return isEmpty(this.design) ? {
        heroImage: {}
      } : {
        heroImage: getImagePaths({
          s3: true,
          path: `/images/design/${this.boxerName}/campaign/${this.boxerName}-${this.$route.params.orientation}`
        }),
        color: this.primaryColor,
        packagingImage: getImagePaths({
          s3: true,
          path: `/images/design/${this.boxerName}/package/${this.boxerName}-${this.$route.params.orientation}`,
          noMobile: true
        }),
        variant: this.variant,
        videoSource: this.hasVideo ? convertSrc(`/images/design/${this.boxerName}/video/${this.boxerName}-${this.$route.params.orientation}.mp4`, true) : '',
        slogan: this.slogan,
        placeholderImage: this.placeholderImage,
        hasVideo: this.hasVideo,
        squareImage: this.squareImage
      }
    },
    ...mapGetters('auth', {
      loggedInUser: AUTH_USER
    })
  },
  methods: {
    getStudioImage ({ slug, orientation }) {
      return getImagePaths({ s3: true, path: `/images/design/${slug}/lookbook/${slug}-${orientation}-studio`, noMobile: true })
    },
    landingPageRedirect ($event) {
      this.$router.push(this.$i18n.localePath({ name: 'landing', params: { landing: OrientationsByGroupIds[$event.id] } }))
    },
    lookbookRedirect (id) {
      this.$router.push(this.$i18n.localePath({ name: 'lookbook-orientation', params: { orientation: OrientationSlugsByGroupIds[id] } }))
    },
    chooseMembership () {
      // socks can only be chosen from the account page and if you are logged in you should also be redirected to the account page
      const queryParams = {}
      let pathName = ''
      const isSocks = this.variant === Orientations.socksoriginals

      if (isSocks || this.loggedInUser) {
        queryParams[GeneralQueryParams.product] = this.variant
        queryParams[GeneralQueryParams.category] = ProductCategoryIds[isSocks ? ProductTypes.socks : ProductTypes.boxers]
        pathName = 'account-memberships-add'
      } else {
        queryParams[SignupQueryParams.orientation] = this.orientationId
        pathName = 'signup'
      }

      this.$router.push({
        path: this.$i18n.localePath({ name: pathName }),
        query: queryParams
      })
    },
    redirectToSingleBoxerPage (slug = '') {
      this.$router.push(this.$i18n.localePath({ name: 'lookbook-orientation-slug', params: { orientation: OrientationsByGroupIds[this.orientationId], slug: slug.toLowerCase() } }))
    },
    ...mapActions('lookbook', {
      getSingleDesign: GET_SINGLE_DESIGN
    })
  },
  head () {
    return {
      title: `Lookbook - ${capitalize(this.boxerName)}`, // TODO: translate
      meta: [
        { hid: 'og:title', name: 'og:title', property: 'og:title', content: `Lookbook - ${capitalize(this.boxerName)}` },
        { hid: 'og:description', name: 'og:description', property: 'og:description', content: this.design.descriptions?.[this.$route.params.orientation] ? this.design.descriptions[this.$route.params.orientation].replace('<p>', '').replace('</p>', '') : OrientationsNamesByGroupIds[this.orientationId] },
        { hid: 'og:image', name: 'og:image', property: 'og:image', content: this.lookbookGridProps.heroImage.mobile ? this.lookbookGridProps.heroImage.mobile.split(',')[0] : '' }
      ]
    }
  }
}
</script>

<style lang="postcss" scoped>
.no-padding-container {
  @apply px-0 !important;
}
</style>
